// F-104 : assistant documentaire du logement, réponses sourcées, absence d'information signalée.
import { useState } from "react";
import { Link } from "react-router";
import { post } from "@core/api";
import { PublicShell } from "@core/layout";
import { date, dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Loading, Notice, PageHeader, Select, Source, Textarea, fieldError, useToast } from "@core/ui";
import { Mode, useAction } from "./shared";

type Scope = { kind: "org"; orgId: string; propertyId?: string; leaseId?: string } | { kind: "lease"; leaseId: string };

const KIND: Record<string, string> = { bail: "Bail", appel_loyer: "Appel de loyer", paiement: "Paiement", solde: "Calcul Bailivo", logement: "Fiche du logement", tache: "Tâche", document: "Document" };

function paths(scope: Scope) {
  if (scope.kind === "lease") return { ask: `/api/v1/leases/${scope.leaseId}/assistant/questions`, history: `/api/v1/leases/${scope.leaseId}/assistant/questions` };
  const qs = new URLSearchParams();
  if (scope.propertyId) qs.set("property_id", scope.propertyId);
  if (scope.leaseId) qs.set("lease_id", scope.leaseId);
  return { ask: `/api/v1/orgs/${scope.orgId}/assistant/questions`, history: `/api/v1/orgs/${scope.orgId}/assistant/questions?${qs}` };
}

function Answer({ a }: { a: any }) {
  const toast = useToast();
  const action = useAction();
  const [report, setReport] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(a.feedback ?? null);
  const send = async (feedback: string, note = "") => {
    const r = await action.run(() => post(`/api/v1/assistant/questions/${a.id}/feedback`, { feedback, note }));
    if (r) {
      setSent(feedback);
      setReport(null);
      toast(feedback === "utile" ? "Merci pour votre retour." : "Erreur signalée.");
    }
  };
  return (
    <div className="plateforme-reponse">
      <p className="row">
        <strong>{a.question}</strong>
        {a.asked_at || a.created_at ? <span className="small muted">{dateTime(a.asked_at ?? a.created_at)}</span> : null}
        <Mode mode={a.mode === "simulateur" ? "simulator" : a.mode} />
      </p>
      {a.missing ? <Notice tone="attention">{a.answer}</Notice> : <div className="plateforme-texte-long">{a.answer}</div>}
      {a.note ? <p className="small muted">{a.note}</p> : null}
      {(a.citations ?? []).length ? (
        <div className="stack-sm">
          <span className="small">Sources</span>
          {a.citations.map((c: any) => (
            <div key={c.id} className="plateforme-citation">
              <Source doc={`${c.id} · ${c.label}`} details={[KIND[c.kind] ?? c.kind, ...(c.date ? [date(c.date)] : [])]} />
              <span className="small">{c.excerpt}</span>
              {c.document_id ? (
                <a className="small" href={`/api/v1/documents/${c.document_id}/fichier`} target="_blank" rel="noreferrer">
                  Ouvrir le document
                </a>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      <ErrorBox error={action.error && !fieldError(action.error, "note") ? action.error : null} />
      {sent ? (
        <span className="small muted">{sent === "utile" ? "Réponse jugée utile." : "Erreur signalée."}</span>
      ) : report !== null ? (
        <div className="stack-sm">
          <Textarea label="Qu’est-ce qui est inexact ?" rows={2} value={report} error={fieldError(action.error, "note")} onChange={(e) => setReport(e.target.value)} />
          <div className="row">
            <Button variant="secondaire" small busy={action.busy} onClick={() => send("erreur_signalee", report)}>
              Signaler l’erreur
            </Button>
            <Button variant="discret" small onClick={() => setReport(null)}>
              Annuler
            </Button>
          </div>
        </div>
      ) : (
        <div className="row">
          <Button variant="discret" small busy={action.busy} onClick={() => send("utile")}>
            Réponse utile
          </Button>
          <Button variant="discret" small onClick={() => setReport("")}>
            Signaler une erreur
          </Button>
        </div>
      )}
    </div>
  );
}

export function AssistantPanel({ scope }: { scope: Scope }) {
  const p = paths(scope);
  const history = useGet<any>(p.history);
  const invalidate = useInvalidate();
  const action = useAction();
  const [question, setQuestion] = useState("");
  const [last, setLast] = useState<any>(null);
  const ask = async () => {
    const body = scope.kind === "lease" ? { question } : { question, property_id: scope.propertyId, lease_id: scope.leaseId };
    const r = await action.run(() => post<any>(p.ask, body));
    if (r) {
      setLast(r);
      setQuestion("");
      invalidate(p.history.split("?")[0]);
    }
  };
  const previous = (history.data?.items ?? []).filter((q: any) => q.id !== last?.id);
  return (
    <div className="stack">
      <Card title="Poser une question">
        <div className="stack-sm">
          <p className="small muted">
            Exemples : « Quel est le montant du dépôt de garantie ? », « Combien a coûté la réparation de la fuite ? », « Reste-t-il un loyer à payer ? ». L’assistant répond à partir des documents et données auxquels vous avez accès, cite ses sources et le dit quand l’information manque. Il ne modifie et n’envoie rien.
          </p>
          <ErrorBox error={action.error && !fieldError(action.error, "question") ? action.error : null} />
          <Textarea label="Votre question" rows={2} value={question} error={fieldError(action.error, "question")} onChange={(e) => setQuestion(e.target.value)} />
          <p>
            <Button busy={action.busy} disabled={question.trim().length < 3} onClick={ask}>
              Demander
            </Button>
          </p>
          {last ? <Answer a={last} /> : null}
        </div>
      </Card>
      {previous.length ? (
        <Card title="Questions précédentes">
          {previous.slice(0, 10).map((q: any) => (
            <Answer key={q.id} a={q} />
          ))}
        </Card>
      ) : null}
    </div>
  );
}

export function OrgAssistantPage() {
  const org = useOrg();
  const scopes = useGet<any>(org.api("/assistant/scopes"));
  const [choice, setChoice] = useState("");
  const [kind, id] = choice.split(":");
  return (
    <div className="page">
      <PageHeader eyebrow="Espace" title="Assistant documentaire" intro="Retrouvez une information dans les documents et données d’un logement ou d’un bail, avec ses sources." />
      {scopes.isLoading ? <Loading /> : null}
      <ErrorBox error={scopes.error} />
      {scopes.data ? (
        scopes.data.properties.length === 0 ? (
          <Empty title="Aucun logement.">Ajoutez un logement et ses documents pour interroger l’assistant.</Empty>
        ) : (
          <Card>
            <Select
              label="Logement ou bail"
              value={choice}
              options={[
                { value: "", label: "Choisissez un logement ou un bail" },
                ...scopes.data.leases.map((l: any) => ({ value: `bail:${l.id}`, label: `Bail : ${l.label}` })),
                ...scopes.data.properties.map((p: any) => ({ value: `logement:${p.id}`, label: `Logement : ${p.label}` })),
              ]}
              onChange={(e) => setChoice(e.target.value)}
            />
          </Card>
        )
      ) : null}
      {choice ? <AssistantPanel key={choice} scope={{ kind: "org", orgId: org.orgId, leaseId: kind === "bail" ? id : undefined, propertyId: kind === "logement" ? id : undefined }} /> : null}
    </div>
  );
}

export function LeaseAssistantTab({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  return <AssistantPanel scope={{ kind: "org", orgId, leaseId }} />;
}

export function PropertyAssistantTab({ orgId, propertyId }: { orgId: string; propertyId: string }) {
  return <AssistantPanel scope={{ kind: "org", orgId, propertyId }} />;
}

/** Côté locataire : documents du bail uniquement. */
export function TenantAssistantSection({ leaseId }: { leaseId: string }) {
  return <AssistantPanel scope={{ kind: "lease", leaseId }} />;
}

export function AccountAssistantPage() {
  const q = useGet<any>("/api/v1/me/assistant/leases");
  const [lease, setLease] = useState("");
  return (
    <PublicShell wide>
      <div className="stack">
        <p className="small">
          <Link to="/moi">Mon espace personnel</Link>
        </p>
        <h1>Assistant de ma location</h1>
        {q.isLoading ? <Loading /> : null}
        <ErrorBox error={q.error} />
        {q.data?.items?.length === 0 ? <Empty title="Aucune location rattachée à votre compte.">L’assistant est disponible pour les locataires dont le bail est géré sur Bailivo.</Empty> : null}
        {q.data?.items?.length ? (
          <Card>
            <Select
              label="Location"
              value={lease}
              options={[{ value: "", label: "Choisissez une location" }, ...q.data.items.map((l: any) => ({ value: l.id, label: l.label }))]}
              onChange={(e) => setLease(e.target.value)}
            />
            <p className="small muted">
              <Badge>Documents du bail</Badge> Seuls les documents partagés avec vous dans le cadre du bail sont consultés.
            </p>
          </Card>
        ) : null}
        {lease ? <AssistantPanel key={lease} scope={{ kind: "lease", leaseId: lease }} /> : null}
      </div>
    </PublicShell>
  );
}
